import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import {
  ArrowLeft, CheckCircle2, Trash2, PieChart, Building2, TrendingUp, ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { formatMKD } from "@/lib/mk-salary";
import { type PayrollRunDetail, type PayrollEntry } from "@/lib/api";
import { MONTH_NAMES_EN, statusConfig, PIE_COLORS } from "./payroll-constants";
import { MiniStat, SalaryDistribution, TotalsBreakdownTable, AdjustmentsSummaryTable } from "./PayrollHelpers";
import { EntrySheet } from "./EntrySheet";

export function PayrollRunDetailView({
  run,
  loading,
  onBack,
  onUpdateEntry,
  onStatusChange,
  onDelete,
  onAddAdjustment,
  onDeleteAdjustment,
  readOnly = false,
  t,
}: {
  run: PayrollRunDetail;
  loading: boolean;
  onBack: () => void;
  onUpdateEntry: (entry: PayrollEntry, amount: number, inputType: "gross" | "net") => void;
  onStatusChange: (status: string, sendEmail: boolean) => void;
  onDelete: () => void;
  onAddAdjustment: (entryId: number, data: { type: string; name: string; amount: number; applyOn: string; notes?: string }) => void;
  onDeleteAdjustment: (entryId: number, adjId: number) => void;
  readOnly?: boolean;
  t: any;
}) {
  const [selectedEntry, setSelectedEntry] = useState<PayrollEntry | null>(null);
  const [confirmStatus, setConfirmStatus] = useState<string | null>(null);
  const [sendEmail, setSendEmail] = useState(false);
  const translatedMonthNames = t("payroll.monthNames", { returnObjects: true });
  const monthNames = Array.isArray(translatedMonthNames) ? translatedMonthNames : MONTH_NAMES_EN;
  const getMonthName = (month: number) => monthNames[month - 1] ?? MONTH_NAMES_EN[month - 1];

  useEffect(() => {
    if (selectedEntry) {
      const updated = run.entries.find((e) => e.id === selectedEntry.id);
      if (updated) setSelectedEntry(updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.entries]);

  const totals = useMemo(() => {
    if (!run.totals) return null;
    const totalGross = run.entries.reduce((s, e) => s + e.grossSalary + (e.totalAdjustmentsGross || 0), 0);
    return { ...run.totals, totalGross };
  }, [run.totals, run.entries]);

  const contributionData = useMemo(() => {
    if (!totals) return [];
    return [
      { name: t("payroll.pension"), value: totals.totalPension },
      { name: t("payroll.health"), value: totals.totalHealth },
      { name: t("payroll.unemployment"), value: totals.totalUnemployment },
      { name: t("payroll.sickness"), value: totals.totalSickness },
      { name: t("payroll.incomeTax"), value: totals.totalTax },
    ].filter((d) => d.value > 0);
  }, [totals, t]);

  const deptData = useMemo(() => {
    const depts: Record<string, { gross: number; net: number }> = {};
    for (const e of run.entries) {
      const dept = e.department || t("payroll.otherDepartment");
      if (!depts[dept]) depts[dept] = { gross: 0, net: 0 };
      depts[dept].gross += e.grossSalary + (e.totalAdjustmentsGross || 0);
      depts[dept].net += e.finalNetSalary || e.netSalary;
    }
    return Object.entries(depts).map(([department, v]) => ({ department, ...v }));
  }, [run.entries, t]);

  const STATUS_FLOW: Record<string, { next: string; label: string }> = {
    Draft: { next: "Calculated", label: t("payroll.markCalculated") },
    Calculated: { next: "Approved", label: t("payroll.markApproved") },
    Approved: { next: "Paid", label: t("payroll.markPaid") },
  };
  const nextStatusInfo = STATUS_FLOW[run.status];
  const nextStatus = nextStatusInfo?.next;
  const nextStatusLabel = nextStatusInfo?.label;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 w-full">
        <div className="flex items-center justify-between animate-in fade-in slide-in-from-left-5 duration-500">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="h-9 w-9 rounded-lg border flex items-center justify-center hover:bg-muted transition-colors">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{getMonthName(run.month)} {run.year}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className={`text-xs border ${statusConfig[run.status]?.color}`}>{t(`payroll.${run.status.toLowerCase()}`, run.status)}</Badge>
                <span className="text-xs text-muted-foreground">{run.entries.length} {t("payroll.employees")}</span>
              </div>
            </div>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2">
              {run.status === "Draft" && (
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={onDelete}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  {t("payroll.deleteRun")}
                </Button>
              )}
              {nextStatus && nextStatusLabel && (
                <Button size="sm" onClick={() => {
                  if (nextStatus === "Approved") { setSendEmail(false); setConfirmStatus(nextStatus); }
                  else { onStatusChange(nextStatus, false); }
                }}>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  {nextStatusLabel}
                </Button>
              )}
            </div>
          )}
        </div>

        {totals && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
            <MiniStat label={t("payroll.totalGross")} value={formatMKD(totals.totalGross)} />
            <MiniStat label={t("payroll.totalNet")} value={formatMKD(totals.totalNet)} />
            <MiniStat label={t("payroll.contributions")} value={formatMKD(totals.totalContributions)} />
            <MiniStat label={t("payroll.incomeTax")} value={formatMKD(totals.totalTax)} />
            <MiniStat label={t("payroll.totalDeductions")} value={formatMKD(totals.totalDeductions)} />
            <MiniStat label={t("payroll.employeeCount")} value={totals.employeeCount.toString()} />
          </div>
        )}

        <Tabs defaultValue="employees" className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto">
            <TabsTrigger value="employees" className="text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              {t("payroll.employees")}
            </TabsTrigger>
            <TabsTrigger value="charts" className="text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              {t("payroll.costBreakdown")}
            </TabsTrigger>
            <TabsTrigger value="breakdown" className="text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              {t("payroll.breakdown")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="employees" className="space-y-3 mt-6">
            {run.entries.map((entry, idx) => {
              const hasAdj = (entry.adjustments || []).length > 0;
              return (
                <Card key={entry.id} className="shadow-card hover:shadow-lg transition-all duration-300 cursor-pointer animate-in fade-in slide-in-from-bottom-4 duration-500 group" style={{ animationDelay: `${idx * 40}ms` }} onClick={() => setSelectedEntry(entry)}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <EmployeeAvatar avatarUrl={entry.avatarUrl} avatarColor={entry.avatarColor || "from-blue-400 to-blue-600"} initials={entry.employeeInitials || "?"} size="sm" className="shrink-0 h-10 w-10 text-xs" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{entry.employeeName}</p>
                      <p className="text-xs text-muted-foreground">{entry.jobTitle} &bull; {entry.department}</p>
                    </div>
                    {hasAdj && <Badge variant="outline" className="text-[10px] bg-purple-100 text-purple-700 border-purple-200 shrink-0">{(entry.adjustments || []).length} {t("payroll.adjustmentAbbrev")}</Badge>}
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-foreground">{t("payroll.gross")}</p>
                      <p className="text-sm font-semibold tabular-nums">{formatMKD(entry.grossSalary + (entry.totalAdjustmentsGross || 0))}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{hasAdj ? t("payroll.finalNet") : t("payroll.net")}</p>
                      <p className="text-sm font-bold text-primary tabular-nums">{formatMKD(entry.finalNetSalary || entry.netSalary)}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
                  </CardContent>
                </Card>
              );
            })}

            <EntrySheet
              entry={selectedEntry}
              open={!!selectedEntry}
              onClose={() => setSelectedEntry(null)}
              isDraft={run.status === "Draft"}
              onUpdateEntry={(entry, amount, inputType) => { onUpdateEntry(entry, amount, inputType); setSelectedEntry(null); }}
              onAddAdjustment={onAddAdjustment}
              onDeleteAdjustment={onDeleteAdjustment}
              readOnly={readOnly}
              t={t}
            />
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {contributionData.length > 0 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <PieChart className="h-4 w-4 text-primary" />
                      {t("payroll.contributionSplit")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <RPieChart>
                        <Pie data={contributionData} cx="50%" cy="50%" innerRadius={60} outerRadius={110} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} animationDuration={1200} animationEasing="ease-out">
                          {contributionData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(value: number) => [`${formatMKD(value)} MKD`, ""]} contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
                      </RPieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {deptData.length > 0 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-500" />
                      {t("payroll.departmentCosts")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={deptData} layout="vertical" barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                        <YAxis type="category" dataKey="department" width={100} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip formatter={(value: number) => [`${formatMKD(value)} MKD`, ""]} contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }} />
                        <Legend />
                        <Bar dataKey="gross" name={t("payroll.gross")} fill="#3b82f6" radius={[0, 4, 4, 0]} animationDuration={1200} />
                        <Bar dataKey="net" name={t("payroll.net")} fill="#10b981" radius={[0, 4, 4, 0]} animationDuration={1200} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {run.entries.length > 0 && (
                <Card className="shadow-card col-span-full animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      {t("payroll.salaryDistribution")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SalaryDistribution entries={run.entries} t={t} />
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="breakdown" className="mt-6 space-y-6">
            {totals && (
              <Card className="shadow-card animate-in fade-in slide-in-from-bottom-4 duration-500">
                <CardHeader>
                  <CardTitle className="text-base">{t("payroll.breakdown")}</CardTitle>
                  <CardDescription>{getMonthName(run.month)} {run.year} — {totals.employeeCount} {t("payroll.employees")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <TotalsBreakdownTable totals={totals} t={t} />
                </CardContent>
              </Card>
            )}
            <AdjustmentsSummaryTable entries={run.entries} t={t} />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={confirmStatus !== null} onOpenChange={() => setConfirmStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("payroll.confirmApproveTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("payroll.confirmApproveMessage", { count: run.entries.length })}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-3 py-2">
            <Checkbox id="send-email-notif" checked={sendEmail} onCheckedChange={(checked) => setSendEmail(!!checked)} />
            <div className="space-y-1">
              <Label htmlFor="send-email-notif" className="font-medium cursor-pointer">{t("payroll.sendEmailNotif")}</Label>
              <p className="text-xs text-muted-foreground">{t("payroll.sendEmailNotifHint")}</p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmStatus) onStatusChange(confirmStatus, sendEmail); setConfirmStatus(null); setSendEmail(false); }}>
              {t("common.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
